unit uPipelineFields;
//
// UDATED: 5/2/26
//    Implements refactored code changes from last week
//

(*
  PROMPT LOCKERS -- Pipeline Field Definitions
  ============================================
  Single source of truth for all field names and their aliases across
  every layer of the app: UI, UIBuffer, DBBuffer, JSON, and DB.

  USAGE OVERVIEW
  --------------
  1. Construct a TPipeline in your form's InitControls, passing in the
     UIBuffer and DBBuffer (both TStringlist).

  2. Call AddField for each field, supplying the field def and optional
     custom procs. Scalar fields with matching names across all layers
     need no custom procs -- the defaults handle them.

  3. Call the appropriate pump method when you need to move data:

       Pipeline.PumpUIToBuffer;      // widgets  --> UIBuffer
       Pipeline.PumpBufferToUI;      // UIBuffer --> widgets
       Pipeline.PumpBufferSync;      // UIBuffer --> DBBuffer (or reverse)
       Pipeline.PumpToJSON;          // DBBuffer --> JSONObj
       Pipeline.PumpFromJSON;        // JSONObj  --> DBBuffer

  EXAMPLE -- simple scalar field (Description):
  ---------------------------------------------
    var fld : TPipelineField;
    fld := TPipelineField.Default('description', 'Description');
    fld.OnUIToBuffer  := procedure begin UIBuffer.Values[fld.InternalName] := Description_edt.Text end;
    fld.OnBufferToUI  := procedure begin Description_edt.Text := UIBuffer.Values[fld.InternalName] end;
    Pipeline.AddField(fld);

  EXAMPLE -- using factory procs for scalar edits (shorthand):
  ------------------------------------------------------------
    Pipeline.AddScalar(fld, Description_edt);
    // generates OnUIToBuffer and OnBufferToUI automatically

  EXAMPLE -- custom proc for Tags array:
  ---------------------------------------
    fld := TPipelineField.Default('tags', 'Tags');
    fld.OnUIToBuffer := procedure
      var i : integer; s : string;
      begin
        s := '';
        for i := 0 to Tags_mmo.Lines.Count-1 do
        begin
          if (s <> '') then s := s + ',';
          s := s + '"' + Tags_mmo.Lines[i] + '"';
        end;
        UIBuffer.Values[fld.InternalName] := '[' + s + ']';
      end;
    fld.OnBufferToUI := procedure
      var i : integer; jarr : TJSONArray;
      begin
        jarr := TJSONObject.ParseJSONValue(UIBuffer.Values[fld.InternalName]) as TJSONArray;
        Tags_mmo.Lines.Clear;
        if Assigned(jarr) then
          for i := 0 to jarr.Count-1 do
            Tags_mmo.Lines.Add(jarr.Items[i].Value);
      end;
    Pipeline.AddField(fld);

  FIELD CATALOG
  -------------
  All known field names are exposed as constants below so string literals
  never appear more than once. Use these everywhere instead of raw strings:

    FN_LOCKER_ID, FN_NAME, FN_TITLE, FN_DESCRIPTION, FN_HOW_TO_USE,
    FN_AI_PROMPT, FN_AI_PLATFORM, FN_AI_MODEL, FN_TEMPERATURE, FN_MAX_TOKENS,
    FN_PRICE, FN_VERSION, FN_AUTHOR, FN_TAGS, FN_PARAMETERS,
    FN_EXECUTION_COUNT, FN_CREATED_AT, FN_LAST_EDITED_AT, FN_LAST_EXECUTED_AT,
    FN_TOTAL_REVENUE,
    FN_HELP_TEXT, FN_HELP_URL
*)

interface

uses
  System.SysUtils, System.Classes, WEBLib.JSON, Web, uStrLists,
  System.StrUtils, System.Generics.Collections;

// ── Field name constants ────────────────────────────────────────────────────
// Use these instead of string literals everywhere in the app.
// A rename here is a single change; the compiler catches every reference.

const
  // NameDescr pipeline
  FN_LOCKER_ID        = 'lockerID';
  FN_NAME             = 'name';
  FN_TITLE            = 'title';
  FN_DESCRIPTION      = 'description';
  FN_HOW_TO_USE       = 'how_to_use_this';
  FN_HELP_TEXT        = 'get_help_text';
  FN_HELP_URL         = 'get_help_url';

  // AI / Prompt pipeline
  FN_AI_PROMPT        = 'ai_prompt';
  FN_AI_PLATFORM      = 'ai_platform_default';
  FN_AI_MODEL         = 'ai_model_default';
  FN_TEMPERATURE      = 'ai_temperature';
  FN_MAX_TOKENS       = 'ai_max_tokens';

  // Misc pipeline
  FN_PRICE            = 'price';
  FN_AUTHOR           = 'author';
  FN_SOURCE           = 'source';

  // Metrics pipeline
  FN_VERSION          = 'version';
  FN_EXECUTION_COUNT  = 'execution_count';
  FN_CREATED_AT       = 'created_at';
  FN_LAST_EDITED_AT   = 'last_edited_at';
  FN_LAST_EXECUTED_AT = 'last_executed_at';
  FN_TOTAL_REVENUE    = 'total_revenue';

  // Complex pipelines
  FN_TAGS             = 'tags';
  FN_PARAMETERS       = 'parameters';

  // Display labels (used in grids and form labels)
  LBL_LOCKER_ID       = 'Locker ID';
  LBL_NAME            = 'Name';
  LBL_TITLE           = 'Title';
  LBL_DESCRIPTION     = 'Description';
  LBL_HOW_TO_USE      = 'How to use';
  LBL_HELP_TEXT       = 'Help Text';
  LBL_HELP_URL        = 'Help URL';

  LBL_AI_PROMPT       = 'AI Prompt';
  LBL_AI_PLATFORM     = 'AI Platform';
  LBL_AI_MODEL        = 'AI Model';
  LBL_TEMPERATURE     = 'Temperature';
  LBL_MAX_TOKENS      = 'Max Tokens';

  LBL_PRICE           = 'Price';
  LBL_AUTHOR          = 'Author';
  LBL_SOURCE          = 'Source';

  LBL_VERSION         = 'Version';
  LBL_EXEC_COUNT      = 'Executions';
  LBL_CREATED_AT      = 'Created';
  LBL_LAST_EDITED     = 'Last Edited';
  LBL_LAST_EXECUTED   = 'Last Executed';
  LBL_TOTAL_REVENUE   = 'Total Sales $';

  LBL_TAGS            = 'Tags';
  LBL_PARAMETERS      = 'Parameters';

// ── Types ───────────────────────────────────────────────────────────────────

type
  // A single field's identity across all layers.
  // InternalName is the canonical name used in UIBuffer, DBBuffer, JSON, and DB.
  // DisplayLabel is what the user sees in grids and labels.
  // JSONKey and DBColumn default to InternalName but can differ if needed.
  // PromptToken is the {{placeholder}} used in the prompt template, if any.

  TPipelineField = record
    InternalName  : string;   // canonical key -- used in buffers, JSON, DB
    DisplayLabel  : string;   // what the user sees
    JSONKey       : string;   // JSON packet key (defaults to InternalName)
    DBColumn      : string;   // DB column name (defaults to InternalName)
    PromptToken   : string;   // '{{token}}' in prompt template, if applicable

    // Pumps -- leave nil to use the default scalar behaviour
    OnUIToBuffer  : TProc;   // widget  --> UIBuffer
    OnBufferToUI  : TProc;   // UIBuffer --> widget
    OnBufferSync  : TProc;   // UIBuffer <-> DBBuffer (nil = straight copy)
    OnToJSON      : TProc;   // DBBuffer --> JSONObj
    OnFromJSON    : TProc;   // JSONObj  --> DBBuffer

    // Convenience constructor -- JSONKey and DBColumn default to InternalName
    class function Default(const aInternalName, aDisplayLabel : string;
                           const aJSONKey    : string = '';
                           const aDBColumn   : string = '';
                           const aPromptToken: string = '') : TPipelineField; static;
  end;

  TPipelineFieldArray = TList<TPipelineField>;

  // A named collection of related fields.
  // Construct one per logical group: NameDescr, AIConfig, Metrics, Tags, Parameters.
  // Pass in UIBuffer and DBBuffer -- these are the TStringlists your form already uses.
  // Pass in JSONObj when you need JSON pumps.

  TPipeline = class
  private
    FName      : string;
    FFields    : TPipelineFieldArray;
    FUIBuffer  : TStringlist;
    FDBBuffer  : TStringlist;
    FJSONObj   : TJSONObject;

    function  FieldCount : integer; inline;
    procedure DefaultBufferSync(const aField: TPipelineField; aToDB: Boolean);
    procedure DefaultToJSON(const aField: TPipelineField);
    procedure DefaultFromJSON(const aField: TPipelineField);

  public
    constructor Create(const aName     : string;
                             aUIBuffer : TStringlist;
                             aDBBuffer : TStringlist;
                             aJSONObj  : TJSONObject = nil);

    procedure AddField(const aField: TPipelineField); inline;

    // -- Pump methods --------------------------------------------------------
    // Call these to move data in bulk across all fields in this pipeline.

    procedure PumpUIToBuffer;    // all widgets  --> UIBuffer
    procedure PumpBufferToUI;    // UIBuffer     --> all widgets
    procedure PumpUIBufferToDB;  // UIBuffer     --> DBBuffer
    procedure PumpDBBufferToUI;  // DBBuffer     --> UIBuffer
    procedure PumpToJSON;        // DBBuffer     --> JSONObj
    procedure PumpFromJSON;      // JSONObj      --> DBBuffer

    // -- Field lookup --------------------------------------------------------
    // Returns a pointer to the field def by internal name.
    // Use this when you need to reference a single field's properties.
    //
    // Example:
    //   f := Pipeline.Field(FN_DESCRIPTION);
    //   ShowMessage(f.DisplayLabel);  // 'Description'

    function Field(const aInternalName: string) : TPipelineField;

    property Name     : string          read FName;
    property Fields   : TPipelineFieldArray read FFields;
    property JSONObj  : TJSONObject     read FJSONObj  write FJSONObj;
    property UIBuffer : TStringlist     read FUIBuffer write FUIBuffer;
    property DBBuffer : TStringlist     read FDBBuffer write FDBBuffer;
  end;

  // A set of pipelines for the whole form.
  // Construct one in InitControls and add your pipelines to it.
  // Then call PumpAll... to move data across every pipeline at once.
  //
  // Example:
  //   Pipelines := TPipelineSet.Create;
  //   Pipelines.Add( NameDescrPipeline );
  //   Pipelines.Add( AIConfigPipeline );
  //   ...
  //   Pipelines.PumpAllUIToBuffer;   // saves all widgets to UIBuffer

  TPipelineSet = class
  private
    FPipelines : TList<TPipeline>;
    procedure PumpAll(aAction: TProc<TPipeline>);
  public
    constructor Create;
    procedure Add(aPipeline: TPipeline);
    procedure PumpAllUIToBuffer;
    procedure PumpAllBufferToUI;
    procedure PumpAllUIBufferToDB;
    procedure PumpAllDBBufferToUI;
    procedure PumpAllToJSON;
    procedure PumpAllFromJSON;
    function  Pipeline(const aName: string): TPipeline;
    function  Count : integer;
  end;


const
  // <visible_name> : <internal_name>
  FIELD_NAME_MAP : array of string = [
    'Locker ID:lockerID',
    'Name:name',
    'Title:title',
    'Description:description',
    'How to use:how_to_use_this',
    'Help Text:get_help_text',
    'Help URL:get_help_url',
    'Source:source',
    'Author:author',
    'Price:price',
    'AI Platform:ai_platform_default',
    'AI Model:ai_model_default',
    'Temperature:ai_temperature',
    'Max Tokens:ai_max_tokens',
    'Version:version',
    'Created:created_at',
    'Last Edited:last_edited_at',
    'Executions:execution_count',
    'Last Executed:last_executed_at',
    'Total Sales $:total_revenue',
    'Tags:tags',
    'Parameters:parameters',
    'AI Prompt:ai_prompt'
  ];

implementation

{ TPipelineField }

//===============================================================
class function TPipelineField.Default(const aInternalName, aDisplayLabel : string;
                                      const aJSONKey     : string = '';
                                      const aDBColumn    : string = '';
                                      const aPromptToken : string = '') : TPipelineField;
//===============================================================
begin
  Result.InternalName  := aInternalName;
  Result.DisplayLabel  := aDisplayLabel;
  Result.JSONKey       := IfThen(aJSONKey   = '', aInternalName, aJSONKey);
  Result.DBColumn      := IfThen(aDBColumn  = '', aInternalName, aDBColumn);
  Result.PromptToken   := aPromptToken;
  Result.OnUIToBuffer  := nil;
  Result.OnBufferToUI  := nil;
  Result.OnBufferSync  := nil;
  Result.OnToJSON      := nil;
  Result.OnFromJSON    := nil;
end;

{ TPipeline }

//===============================================================
constructor TPipeline.Create(const aName     : string;
                                   aUIBuffer : TStringlist;
                                   aDBBuffer : TStringlist;
                                   aJSONObj  : TJSONObject = nil);
//===============================================================
begin
  FName     := aName;
  FUIBuffer := aUIBuffer;
  FDBBuffer := aDBBuffer;
  FJSONObj  := aJSONObj;
  FFields := TList<TPipelineField>.Create;
end;

//===============================================================
procedure TPipeline.AddField(const aField: TPipelineField);
//===============================================================
begin
  FFields.Add(aField);
end;

//===============================================================
function TPipeline.FieldCount: integer;
//===============================================================
begin
  Result := FFields.Count
end;

//===============================================================
function TPipeline.Field(const aInternalName: string): TPipelineField;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
    if (f.InternalName = aInternalName) then
    begin
      //Result := f;
      Exit(f);
    end;
  raise Exception.CreateFmt('TPipeline.Field: unknown field "%s"', [aInternalName]);
end;

//===============================================================
procedure TPipeline.DefaultBufferSync(const aField: TPipelineField; aToDB: Boolean);
//===============================================================
begin
  if aToDB then
    FDBBuffer.Values[aField.InternalName] := FUIBuffer.Values[aField.InternalName]
  else
    FUIBuffer.Values[aField.InternalName] := FDBBuffer.Values[aField.InternalName];
end;

//===============================================================
procedure TPipeline.DefaultToJSON(const aField: TPipelineField);
//===============================================================
begin
  if not Assigned(FJSONObj) then Exit;
  FJSONObj.RemovePair(aField.JSONKey);
  FJSONObj.AddPair(aField.JSONKey,
    TJSONString.Create(FDBBuffer.Values[aField.InternalName]));
end;

//===============================================================
procedure TPipeline.DefaultFromJSON(const aField: TPipelineField);
//===============================================================
var v : TJSONValue;
begin
  if not Assigned(FJSONObj) then Exit;
  v := FJSONObj.GetValue(aField.JSONKey);
  if Assigned(v) then
    FDBBuffer.Values[aField.InternalName] := v.Value;
end;

//===============================================================
procedure TPipeline.PumpUIToBuffer;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
    if Assigned(f.OnUIToBuffer) then
      f.OnUIToBuffer;
  // scalar fields with no proc are handled by the form's existing buffer logic
  // until all fields have explicit procs wired up
end;

//===============================================================
procedure TPipeline.PumpBufferToUI;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
    if Assigned(f.OnBufferToUI) then
      f.OnBufferToUI;
end;

//===============================================================
procedure TPipeline.PumpUIBufferToDB;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
  begin
    if Assigned(f.OnBufferSync) then
      f.OnBufferSync
    else
      DefaultBufferSync(f, True);  // UIBuffer --> DBBuffer
  end;
end;

//===============================================================
procedure TPipeline.PumpDBBufferToUI;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
  begin
    if Assigned(f.OnBufferSync) then
      f.OnBufferSync
    else
      DefaultBufferSync(f, False);  // DBBuffer --> UIBuffer
  end;
end;

//===============================================================
procedure TPipeline.PumpToJSON;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
  begin
    if Assigned(f.OnToJSON) then
      f.OnToJSON
    else
      DefaultToJSON(f);
  end;
end;

//===============================================================
procedure TPipeline.PumpFromJSON;
//===============================================================
var f : TPipelineField;
begin
  for f in FFields do
  begin
    if Assigned(f.OnFromJSON) then
      f.OnFromJSON
    else
      DefaultFromJSON(f);
  end;
end;

{ TPipelineSet }

//===============================================================
constructor TPipelineSet.Create;
//===============================================================
begin
  FPipelines := TList<TPipeline>.Create;
end;

//===============================================================
function TPipelineSet.Count: integer;
//===============================================================
begin
  Result := FPipelines.Count;
end;

//===============================================================
procedure TPipelineSet.Add(aPipeline: TPipeline);
//===============================================================
begin
  FPipelines.Add(aPipeline);
end;

//===============================================================
function TPipelineSet.Pipeline(const aName: string): TPipeline;
//===============================================================
var p : TPipeline;
begin
  for p in FPipelines do
    if (p.Name = aName) then
    begin
      Result := p;
      Exit;
    end;
  raise Exception.CreateFmt('TPipelineSet.Pipeline: unknown pipeline "%s"', [aName]);
end;

//===============================================================
procedure TPipelineSet.PumpAll(aAction: TProc<TPipeline>);
//===============================================================
var p : TPipeline;
begin
  for p in FPipelines do aAction(p);
end;

procedure TPipelineSet.PumpAllUIToBuffer;    begin PumpAll(procedure(p: TPipeline) begin p.PumpUIToBuffer    end) end;
procedure TPipelineSet.PumpAllBufferToUI;    begin PumpAll(procedure(p: TPipeline) begin p.PumpBufferToUI    end) end;
procedure TPipelineSet.PumpAllUIBufferToDB;  begin PumpAll(procedure(p: TPipeline) begin p.PumpUIBufferToDB  end) end;
procedure TPipelineSet.PumpAllDBBufferToUI;  begin PumpAll(procedure(p: TPipeline) begin p.PumpDBBufferToUI  end) end;
procedure TPipelineSet.PumpAllToJSON;        begin PumpAll(procedure(p: TPipeline) begin p.PumpToJSON        end) end;
procedure TPipelineSet.PumpAllFromJSON;      begin PumpAll(procedure(p: TPipeline) begin p.PumpFromJSON      end) end;

end.
